import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { useLog } from '../context/LogContext';
import { useAuth } from '../context/AuthContext';

const USE_TEST_STORE = true;

export const DevConsole: React.FC = () => {
    const { logs, clearLogs } = useLog();
    const { isDevVersion } = useAuth();
    const consoleRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new logs arrive
    useEffect(() => {
        if (consoleRef.current) {
            consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
        }
    }, [logs]);

    // Only show in dev mode
    if (!USE_TEST_STORE || !isDevVersion) {
        return null;
    }

    return (
        <Box sx={{
            mb: 2,
            p: 2,
            bgcolor: '#1e1e1e',
            borderRadius: 1,
            border: '2px solid #2196f3',
            fontFamily: 'monospace',
            fontSize: '0.70rem',
            maxWidth: 'md',
            mx: 'auto'
        }}>
            <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                color: '#2196f3',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>🖥️ DEVELOPMENT CONSOLE</span>
                <button
                    onClick={clearLogs}
                    style={{
                        backgroundColor: '#333',
                        color: '#fff',
                        border: '1px solid #555',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '0.65rem'
                    }}
                >
                    Clear
                </button>
            </div>
            <div
                ref={consoleRef}
                style={{
                    backgroundColor: '#0d0d0d',
                    padding: '8px',
                    borderRadius: '4px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid #333'
                }}
            >
                {logs.length === 0 ? (
                    <div style={{ color: '#666', fontStyle: 'italic' }}>No logs yet...</div>
                ) : (
                    logs.map((log, index) => {
                        const colors = {
                            info: '#4fc3f7',
                            warn: '#ffb74d',
                            error: '#ef5350',
                            debug: '#9e9e9e'
                        };
                        const color = colors[log.level];

                        return (
                            <div key={index} style={{
                                marginBottom: '4px',
                                paddingBottom: '4px',
                                borderBottom: '1px solid #222'
                            }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                    <span style={{ color: '#666', minWidth: '80px' }}>{log.timestamp}</span>
                                    <span style={{
                                        color: color,
                                        minWidth: '50px',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase'
                                    }}>
                                        {log.level}
                                    </span>
                                    <span style={{ color: '#e0e0e0', flex: 1, wordBreak: 'break-word' }}>
                                        {log.message}
                                    </span>
                                </div>
                                {log.data && (
                                    <div style={{
                                        marginLeft: '138px',
                                        marginTop: '4px',
                                        color: '#aaa',
                                        fontSize: '0.65rem',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {typeof log.data === 'object' ? JSON.stringify(log.data, null, 2) : String(log.data)}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Quick State Info */}
            <div style={{
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid #333',
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                fontSize: '0.65rem'
            }}>
                <div style={{ color: '#aaa' }}>
                    Logs: <span style={{ color: '#2196f3' }}>{logs.length}/100</span>
                </div>
                <div style={{ color: '#666', fontStyle: 'italic' }}>
                    Dev Mode Active
                </div>
            </div>
        </Box>
    );
};
